/**
 * frontend/src/lib/langueBootstrap.ts
 * `#814` — **LA LANGUE SE RÉSOUT AVANT LE PREMIER PIXEL — ET ELLE ÉTAIT DÉJÀ LÀ.**
 *
 * ⚠⚠ **LE FLASH NE VENAIT PAS D'UNE LANGUE INCONNUE.** *`userStore` est un
 *   `persist` zustand (clé `orbicore-user`) dont le `partialize` embarque `user`
 *   — donc `User.language` — et il se réhydrate **synchronement, avant le premier
 *   rendu**. On avait la langue sous la main ; on ne l'appliquait pas : `/auth/me`
 *   la posait depuis un `useEffect` d'`AppShell` (`appliquerLangue` dans
 *   `useAuth.fetchMe`), et un `useEffect` s'exécute **après le premier paint**.
 *   D'où : l'écran se peint en FR, puis bascule.*
 *
 * ⚠ **AUCUNE PERSISTANCE NOUVELLE** (décision Hkm) : *on lit le store existant,
 *   tel qu'il est déjà écrit — on n'ajoute aucune clé navigateur pour la langue.*
 *
 * ⚠ **`/auth/me` RESTE LA VÉRITÉ** : *ceci ne fait qu'éviter le flash ; le compte
 *   re-pilote la langue juste après (`useAuth.fetchMe` → `appliquerLangue`), et
 *   corrige si elle a changé côté serveur.*
 *
 * ⚠ **AU CHARGEMENT, PAS DANS UN COMPOSANT** — *même raison que le thème dans
 *   `main.tsx` : depuis un composant, c'est déjà trop tard.*
 */
import { useUserStore } from '@/stores/userStore';
import { appliquerLangue } from './i18n';

/**
 * Applique la langue du compte déjà réhydraté. Appelé par `main.tsx` AVANT
 * `createRoot(...).render(...)`.
 *
 * ⚠ **CAS NOMMÉ — compte absent ou sans langue** (premier login, `localStorage`
 *   refusé) : *on reste sur le défaut `fr`, et **aucun flash n'est possible** —
 *   `LoginPage` attend `await fetchMe()` avant de naviguer, donc la langue est
 *   posée avant que le shell ne monte.*
 */
export function appliquerLangueDuCompte(): void {
  const langue = useUserStore.getState().user?.language;
  if (langue === 'fr' || langue === 'en') { appliquerLangue(langue); return; }
  appliquerReplNavigateurAvantConnexion();
}

/**
 * ══════════════════════════════════════════════════════════════════════════════
 * `#840` ★3 — **AVANT LE LOGIN, IL N'Y A PAS DE COMPTE.**
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * ⚠⚠ **C'EST UNE EXCEPTION À UNE RÈGLE GRAVÉE, ET ELLE EST NOMMÉE.** *Le `#795`
 *   dit : **la langue vient du compte, point** — jamais d'`Accept-Language`,
 *   jamais de préférence navigateur. Cette règle suppose un compte. **Sur
 *   `/login`, `/2fa` et `/2fa/enroll`, il n'y en a pas** : l'écran qui refuse
 *   une connexion parlait donc français à un anglophone qui n'a encore rien pu
 *   choisir.*
 *
 * ⚠ **PORTÉE STRICTE, ET VÉRIFIABLE** : *ces trois routes, et elles seules. Dès
 *   que `/auth/me` répond, `useAuth.fetchMe` appelle `appliquerLangue(me.language)`
 *   et **le compte reprend la main** — y compris pour l'écraser. Un compte FR
 *   sur un navigateur EN redevient français après connexion.*
 *
 * ⚠ **AUCUNE PERSISTANCE, TOUJOURS** *(décision Hkm cache-logout)* : *on **lit**
 *   `navigator.language`, on n'écrit rien. Rien n'est stocké côté navigateur.*
 */
const ROUTES_AVANT_CONNEXION = ['/login', '/2fa', '/2fa/enroll'];

export function appliquerReplNavigateurAvantConnexion(): void {
  if (typeof window === 'undefined') return;
  const chemin = window.location.pathname;
  if (!ROUTES_AVANT_CONNEXION.some((r) => chemin === r || chemin.startsWith(`${r}/`))) return;
  // ⚠ `fr` reste le défaut : tout ce qui n'est pas explicitement anglais l'est.
  const brut = (navigator.language || '').toLowerCase();
  appliquerLangue(brut.startsWith('en') ? 'en' : 'fr');
}
